import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import recordsService from '../../../services/recordsService';
import VehicleDiagram from './VehicleDiagram';
import { DUE_SOURCES, PAST_DUE_CHIP, STATUS, STATUS_ORDER } from './zoneStyles';

// The map with every list it reads defaulted, once, where it arrives. WHY: the portal has no error boundary, so one
// missing list in a response would blank the member's whole page instead of this card.
const normalizeMap = (result) => ({
  ...result,
  zones: (result?.zones || []).map((zone) => ({ ...zone, due: zone.due || [], declined: zone.declined || [] })),
});

const formatDate = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : null;
const formatMiles = (miles) => (miles ? `${Number(miles).toLocaleString()} mi` : null);
const formatPrice = (price) => (price ? Number(price).toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : null);
const joined = (...parts) => parts.filter(Boolean).join(' · ');

const Badge = ({ number, status }) => (
  <Box
    component="span"
    sx={{
      width: 24,
      height: 24,
      borderRadius: '50%',
      border: `2px solid ${STATUS[status].color}`,
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 700,
      flex: '0 0 auto',
    }}
  >
    {number}
  </Box>
);

const zoneLines = (zone) => {
  if (zone.status === 'declined') {
    return zone.declined.map((item) => ({
      key: item.title,
      title: item.title,
      price: formatPrice(item.price),
      meta: joined(item.date && `Declined ${formatDate(item.date)}`, item.mileage && `at ${formatMiles(item.mileage)}`),
    }));
  }
  if (zone.status === 'due') {
    return zone.due.map((item) => ({
      key: item.title,
      title: item.title,
      meta: joined(
        `Due ${formatDate(item.due_when)}`,
        item.due_mileage && `or at ${formatMiles(item.due_mileage)}`,
        DUE_SOURCES[item.source],
      ),
    }));
  }
  const { serviced } = zone;
  const next = zone.due[0];
  return [
    {
      key: 'serviced',
      title: serviced.title !== zone.label ? serviced.title : null,
      meta: joined(formatDate(serviced.date), formatMiles(serviced.mileage), serviced.location),
    },
    next && {
      key: 'next',
      title: null,
      meta: joined(`Next: ${next.title}`, formatDate(next.due_when), next.due_mileage && `or at ${formatMiles(next.due_mileage)}`),
    },
  ].filter(Boolean);
};

/**
 * One vehicle's service map: the outline, a legend, and the same parts as numbered lists under "Serviced recently",
 * "Due" and "You declined" (CIN-7192). Shown on the member's page when the company turns the map on.
 */
const VehicleServiceMap = ({ groupId, vehicleId }) => {
  const [map, setMap] = useState(null);
  const [error, setError] = useState(null);
  // On a phone the outline shrinks and its legend sits beside it. WHY: at full size the outline and a stacked legend
  // filled a whole phone screen before the lists, which say what was actually done.
  const isPhone = useMediaQuery(useTheme().breakpoints.down('sm'));

  useEffect(() => {
    let cancelled = false;
    setMap(null);
    setError(null);
    recordsService
      .getServiceMap(groupId, vehicleId)
      .then((result) => {
        if (!cancelled) setMap(normalizeMap(result));
      })
      .catch(() => {
        if (!cancelled) setError("We couldn't load this vehicle's service history right now.");
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, vehicleId]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!map) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!map.zones.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing to show for this vehicle yet.
      </Typography>
    );
  }

  // Number the parts in the order the lists show them, so a badge on the outline matches a list item.
  const ordered = STATUS_ORDER.flatMap((status) => map.zones.filter((zone) => zone.status === status));
  const numbers = Object.fromEntries(ordered.map((zone, index) => [zone.key, index + 1]));
  const statuses = Object.fromEntries(map.zones.map((zone) => [zone.key, zone.status]));
  const spoken = STATUS_ORDER.map((status) => {
    const labels = map.zones.filter((zone) => zone.status === status).map((zone) => zone.label.toLowerCase());
    return labels.length ? `${STATUS[status].heading.toLowerCase()}: ${labels.join(', ')}` : null;
  })
    .filter(Boolean)
    .join('. ');

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
      <Box
        component="figure"
        sx={{
          m: 0,
          flex: { xs: '1 1 100%', sm: '0 0 220px' },
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: 'center',
          gap: { xs: 2, sm: 1.75 },
        }}
      >
        <VehicleDiagram
          statuses={statuses}
          numbers={numbers}
          width={isPhone ? 150 : 220}
          badgeScale={isPhone ? 1.25 : 1}
          label={`Top view of the vehicle. ${spoken}.`}
        />
        <Box
          component="figcaption"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.9,
            fontSize: 12,
            color: 'text.secondary',
            alignSelf: { xs: 'center', sm: 'stretch' },
            flex: { xs: '1 1 0', sm: 'none' },
            minWidth: 0,
          }}
        >
          {STATUS_ORDER.map((status) => (
            <Box key={status} component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 12,
                  borderRadius: '3px',
                  flex: '0 0 auto',
                  boxSizing: 'border-box',
                  ...(status === 'serviced' && { background: STATUS.serviced.fill }),
                  ...(status === 'due' && { background: STATUS.due.fill, border: `1.5px dashed ${STATUS.due.stroke}` }),
                  ...(status === 'declined' && {
                    border: `2px solid ${STATUS.declined.stroke}`,
                    background: `repeating-linear-gradient(45deg, ${STATUS.declined.stroke} 0 2px, ${STATUS.declined.tint} 2px 5px)`,
                  }),
                }}
              />
              {STATUS[status].label}
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {STATUS_ORDER.map((status) => {
          const zones = ordered.filter((zone) => zone.status === status);
          if (!zones.length) return null;
          return (
            <Box key={status} component="section">
              <Typography
                component="h3"
                sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.06em', color: STATUS[status].color, mt: 0, mb: 1.25 }}
              >
                {STATUS[status].heading}
              </Typography>
              <Stack component="ol" spacing={1.5} sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {zones.map((zone) => (
                  <Box component="li" key={zone.key} sx={{ display: 'flex', gap: 1.5 }}>
                    <Badge number={numbers[zone.key]} status={status} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{zone.label}</Typography>
                        {status === 'due' && zone.due.some((item) => item.past_due) && (
                          <Box
                            component="span"
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              ...PAST_DUE_CHIP,
                              borderRadius: '10px',
                              px: 1,
                              py: 0.25,
                            }}
                          >
                            Past due
                          </Box>
                        )}
                      </Box>
                      {zoneLines(zone).map((line) => (
                        <Box key={line.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25 }}>
                          <Typography variant="body2" color="text.secondary">
                            {joined(line.title, line.meta)}
                          </Typography>
                          {line.price && (
                            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {line.price}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          );
        })}
        <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
          {map.visits
            ? `Built from your ${map.visits} ${map.visits === 1 ? 'visit' : 'visits'} since ${new Date(
                `${map.since}T12:00:00`,
              ).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })} and your shop's recommendations.`
            : "Built from your shop's recommendations."}{' '}
          Shop fees and supplies aren&apos;t parts, so they aren&apos;t shown here.
        </Typography>
      </Box>
    </Box>
  );
};

export default VehicleServiceMap;
